import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_URL } from '../../utils/BASE_URL';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const initialState = {
    projects: [],
    project: null,
    loading: false,
    error: null,
    message: null
};

// Handle Errors helper
const handleErrors = (error, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

// Create Project
export const createProject = createAsyncThunk(
    'project/create',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/projects`, data, { withCredentials: true });
            toast.success(response.data.message || 'Project created successfully');
            return response.data;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to create project';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

// Get All Projects
export const getAllProjects = createAsyncThunk(
    'project/getAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BASE_URL}/projects`);
            return response.data.data;
        } catch (error) {
            return handleErrors(error, rejectWithValue);
        }
    },
    {
        condition: (_, { getState }) => {
            const { project } = getState();
            if (project.loading || project.projects.length > 0) {
                return false;
            }
        }
    }
);

// Get Project By ID
export const getProjectById = createAsyncThunk(
    'project/getById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BASE_URL}/projects/${id}`);
            return response.data;
        } catch (error) {
            return handleErrors(error, rejectWithValue);
        }
    }
);

// Get Project By SKU
export const getProjectBySku = createAsyncThunk(
    'project/getBySku',
    async (sku, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BASE_URL}/projects?sku=${sku}`);
            return response.data.data[0];
        } catch (error) {
            return handleErrors(error, rejectWithValue);
        }
    }
);


// Update Project
export const updateProject = createAsyncThunk(
    'project/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${BASE_URL}/projects/${id}`, data, { withCredentials: true });
            toast.success(response.data.message || 'Project updated successfully');
            return response.data;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to update project';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

// Delete Project
export const deleteProject = createAsyncThunk(
    'project/delete',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${BASE_URL}/projects/${id}`, { withCredentials: true });
            toast.success(response.data.message || 'Project deleted successfully');
            return { id, message: response.data.message };
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to delete project';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

export const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        clearMessage: (state) => {
            state.message = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetProject: (state) => {
            state.project = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create
            .addCase(createProject.pending, (state) => {
                state.loading = true;
            })
            .addCase(createProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projects.unshift(action.payload.project);
                state.message = action.payload.message;
            })
            .addCase(createProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Get All
            .addCase(getAllProjects.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.projects = action.payload;
            })
            .addCase(getAllProjects.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Get By ID
            .addCase(getProjectById.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProjectById.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload.project;
            })
            .addCase(getProjectById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Get By SKU
            .addCase(getProjectBySku.pending, (state) => {
                state.loading = true;
            })
            .addCase(getProjectBySku.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload;
            })
            .addCase(getProjectBySku.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Update
            .addCase(updateProject.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateProject.fulfilled, (state, action) => {
                state.loading = false;
                state.project = action.payload.project;
                const index = state.projects.findIndex(p => p._id === action.payload.project._id);
                if (index !== -1) {
                    state.projects[index] = action.payload.project;
                }
                state.message = action.payload.message;
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Delete
            .addCase(deleteProject.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteProject.fulfilled, (state, action) => {
                state.loading = false;
                state.projects = state.projects.filter(p => p._id !== action.payload.id);
                state.message = action.payload.message;
            })
            .addCase(deleteProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            });
    }
});

export const { clearMessage, clearError, resetProject } = projectSlice.actions;
export default projectSlice.reducer;
